/**
 * PDF Export Utilities
 * Provides functionality to export data and HTML elements to PDF
 */

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Export HTML element to PDF
 * @param {HTMLElement} element - The HTML element to export
 * @param {string} filename - The PDF filename
 * @param {Object} options - Configuration options
 */
export const exportElementToPDF = async (element, filename, options = {}) => {
  const {
    orientation = 'portrait',
    unit = 'mm',
    format = 'a4',
    margin = 10,
    scale = 2,
  } = options;

  try {
    const canvas = await html2canvas(element, {
      scale,
      useCORS: true,
      logging: false,
    });

    const imgData = canvas.toDataURL('image/png');
    const imgWidth = orientation === 'landscape' ? 297 - margin * 2 : 210 - margin * 2;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    const pdf = new jsPDF({
      orientation,
      unit,
      format,
    });

    let heightLeft = imgHeight;
    let position = margin;

    pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
    heightLeft -= pdf.internal.pageSize.getHeight() - margin * 2;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight + margin;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
      heightLeft -= pdf.internal.pageSize.getHeight() - margin * 2;
    }

    pdf.save(filename);
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

/**
 * Generate a PDF with tables and formatted data
 * @param {string} title - PDF title
 * @param {Array} data - Array of objects to convert to table
 * @param {Array} columns - Column definitions [{key: 'name', label: 'Name'}, ...]
 * @param {string} filename - The PDF filename
 */
export const exportTableToPDF = (title, data, columns, filename) => {
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 10;
  let yPosition = margin + 10;

  // Add title
  pdf.setFontSize(16);
  pdf.text(title, margin, yPosition);
  yPosition += 15;

  // Add date
  pdf.setFontSize(10);
  pdf.text(`Generado: ${new Date().toLocaleDateString('es-ES')}`, margin, yPosition);
  yPosition += 10;

  // Column widths
  const columnWidths = columns.map(() => (pageWidth - margin * 2) / columns.length);

  // Table headers
  pdf.setFontSize(11);
  pdf.setFont(undefined, 'bold');
  columns.forEach((col, i) => {
    const xPos = margin + columnWidths.slice(0, i).reduce((a, b) => a + b, 0);
    pdf.text(col.label, xPos, yPosition, { maxWidth: columnWidths[i] - 1 });
  });
  yPosition += 8;

  // Table rows
  pdf.setFont(undefined, 'normal');
  pdf.setFontSize(9);

  data.forEach((row) => {
    let rowHeight = 6;
    columns.forEach((col, i) => {
      const xPos = margin + columnWidths.slice(0, i).reduce((a, b) => a + b, 0);
      const cellValue = String(row[col.key] || '');
      const splitText = pdf.splitTextToSize(cellValue, columnWidths[i] - 1);
      rowHeight = Math.max(rowHeight, splitText.length * 6);
    });

    // Check if we need a new page
    if (yPosition + rowHeight > pageHeight - margin) {
      pdf.addPage();
      yPosition = margin;
    }

    // Draw row
    columns.forEach((col, i) => {
      const xPos = margin + columnWidths.slice(0, i).reduce((a, b) => a + b, 0);
      const cellValue = String(row[col.key] || '');
      pdf.text(cellValue, xPos, yPosition, { maxWidth: columnWidths[i] - 1 });
    });
    yPosition += rowHeight + 2;
  });

  pdf.save(filename);
};

/**
 * Export dashboard snapshot as PDF
 * @param {string} filename - The PDF filename
 */
export const exportDashboardToPDF = async (filename = 'dashboard.pdf') => {
  const dashboardElement = document.querySelector('.onebase-dashboard');
  if (!dashboardElement) {
    throw new Error('Dashboard element not found');
  }
  return exportElementToPDF(dashboardElement, filename);
};

/**
 * Format currency for PDF export
 * @param {number} value - The value to format
 * @param {string} currency - Currency code (default: CLP)
 */
export const formatCurrencyForPDF = (value, currency = 'CLP') => {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);
};
